import React from 'react';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import SearchBar from '../../../../../../shared/designSystem/components/SearchBar';
import { formatCurrency2SigFig, formatWholeNumber } from '../../../../../../../core/utils/campaigns';

// =============================================================================
// BasketOrdersSection — Orders list browser on the V2 Basket Items tab.
//
// Replaces v1's cramped left-sidebar layout with a proper full-width
// section card. Server-side paginated DataTable showing one row per order
// id; clicking a row expands to show the line items that made up that
// order (name / quantity / price / total).
//
// Search is an orderId prefix match handled by the parent (controller
// refetches on each `onSearch` call).
// =============================================================================

const orderIdSort = (rowA, rowB) =>
  String(rowA.original.orderId || '').localeCompare(String(rowB.original.orderId || ''));

const BasketOrdersSection = ({
  orders = [],
  total = 0,
  currentPage = 1,
  pageSize = 10,
  loading = false,
  onPageChange,
  searchTerm,
  onSearchChange,
  onSearch,
}) => {
  // Note: v1 exposed `expandedRowKeys` + `onExpand` for the controller to
  // track which orders had been opened (possibly to lazy-load details). V2
  // basket items already carry their full item list in the initial payload,
  // so DataTable owns expand state directly and those props are no longer
  // needed. Kept for forward-compat at the call site (BasketItemsTab) in
  // case a future backend shape reintroduces lazy-loading.
  // Single-column layout matching the narrow sidebar. The item count isn't
  // surfaced here because the expand chevron already signals "details
  // inside" and the 25% pane doesn't have room for a numeric column
  // without horizontal scroll. Order count total is visible in the
  // pagination footer.
  const columns = [
    {
      id: 'orderId',
      accessorKey: 'orderId',
      header: 'Order ID',
      sortable: true,
      sortingFn: orderIdSort,
      meta: { align: 'left', width: 200 },
      cell: ({ getValue, row }) => {
        const id = getValue() || '—';
        const count = (row.original.basketItems || []).length;
        return (
          <div className="campaign-overview__basket-orders-row">
            <span className="campaign-overview__creatives-name">{id}</span>
            {count > 0 && (
              <span className="campaign-overview__basket-orders-count">
                {formatWholeNumber(count)} item{count === 1 ? '' : 's'}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  // Expanded row renders the order's line items as a clean list.
  const renderExpandedRow = (record) => {
    const items = record.basketItems || [];
    if (items.length === 0) {
      return (
        <div className="campaign-overview__basket-items-empty">
          No items in this order.
        </div>
      );
    }
    return (
      <ul className="campaign-overview__basket-items-list">
        {items.map((item, i) => (
          <li key={i} className="campaign-overview__basket-items-line">
            <span className="campaign-overview__basket-items-line-idx">
              {i + 1}.
            </span>
            <div className="campaign-overview__basket-items-line-body">
              <div className="campaign-overview__basket-items-line-name">
                {item.name}
              </div>
              <div className="campaign-overview__basket-items-line-meta">
                <span>Qty <strong>{formatWholeNumber(item.quantity)}</strong></span>
                <span>Price <strong>{formatCurrency2SigFig(item.price)}</strong></span>
                <span>Total <strong>{formatCurrency2SigFig(item.total)}</strong></span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const renderMobileCard = (record) => {
    const count = (record.basketItems || []).length;
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <div className="campaign-overview__creatives-card-name">
            <span>{record.orderId || '—'}</span>
          </div>
        </div>
        <div className="campaign-overview__basket-orders-row">
          <span className="campaign-overview__basket-orders-count">
            {formatWholeNumber(count)} item{count === 1 ? '' : 's'}
          </span>
        </div>
      </>
    );
  };

  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    if (onPageChange) onPageChange(pageIndex + 1, newPageSize);
  };

  // Bridge DataTable's expand state (TanStack) to the parent's
  // `expandedRowKeys` / `onExpand(expanded, record)` API used by the
  // controller (keeps backend-driven per-order detail loading if present).
  const bridgedExpand = {
    isRowExpandable: (record) => (record.basketItems || []).length > 0,
    renderExpandedRow,
  };

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          Orders
        </div>
        <SearchBar
          placeholder="Search by order ID..."
          value={searchTerm || ''}
          onChange={onSearchChange}
          onSearch={onSearch}
          className="campaign-overview__creatives-search"
        />
      </header>

      {loading ? (
        <div style={{ padding: 16 }}>
          <span
            className="skeleton"
            style={{ width: '100%', height: 220, borderRadius: 12, display: 'block' }}
          />
        </div>
      ) : (
        <div className="campaign-overview__creatives-body">
          <div className="campaign-overview__creatives-table">
            <DataTable
              data={orders || []}
              columns={columns}
              pageSize={pageSize || 10}
              pageSizeOptions={[10, 20, 50, 100]}
              mobileBreakpoint="md"
              renderMobileCard={renderMobileCard}
              manualPagination
              pageIndex={(currentPage || 1) - 1}
              totalRows={total}
              onPaginationChange={handlePaginationChange}
              expandable={bridgedExpand}
              emptyState="No orders match the current filter."
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default BasketOrdersSection;
